import { TrendingUp, AlertCircle, Wrench, FileText, Plus, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuthStore } from '@/store/useAuthStore';

// Workflow stats with modern design
const workflowStats = [
  {
    label: 'Lease Applications',
    count: 3,
    amount: null,
    status: 'New',
    color: 'bg-orange-500',
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600',
    details: ['Pending review (2)', 'Background check (1)'],
  },
  {
    label: 'Rent Collection',
    count: 12,
    amount: 18500,
    status: 'Due this week',
    color: 'bg-red-500',
    bgColor: 'bg-red-50',
    iconColor: 'text-red-600',
    details: ['Overdue (2)', 'Due in 3 days (10)'],
  },
  {
    label: 'Work Orders',
    count: 5,
    amount: null,
    status: 'In Progress',
    color: 'bg-green-500',
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    details: ['Scheduled (3)', 'Awaiting parts (2)'],
  },
  {
    label: 'Lease Renewals',
    count: 8,
    amount: null,
    status: 'Expiring soon',
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    details: ['This month (4)', 'Next month (4)'],
  },
];

const revenueData = [
  { month: 'Jan', revenue: 32000 },
  { month: 'Feb', revenue: 35000 },
  { month: 'Mar', revenue: 38000 },
  { month: 'Apr', revenue: 42000 },
  { month: 'May', revenue: 45000 },
  { month: 'Jun', revenue: 48000 },
];

export default function Dashboard() {
  const { user } = useAuthStore();
  const firstName = user?.firstName || 'there';
  
  const currentDate = new Date().toLocaleDateString('en-CA', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });
  
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Modern Gradient Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-8 shadow-strong">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="relative">
          <div className="mb-6">
            <p className="text-sm font-medium text-primary-100">{currentDate}</p>
            <h1 className="mt-2 text-4xl font-bold text-white">{getGreeting()}, {firstName}!</h1>
          </div>
          
          {/* Status Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
            <CheckCircle className="h-4 w-4 text-green-300" />
            <span className="text-sm font-medium text-white">Everything is up to date - great work!</span>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Button className="bg-white text-primary-700 hover:bg-primary-50 shadow-md">
              <Plus className="mr-2 h-4 w-4" />
              Add Property
            </Button>
            <Button variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm">
              Create Maintenance Request
            </Button>
          </div>
        </div>
      </div>

      {/* Workflow Section - Modern Cards */}
      <div>
        <h2 className="mb-5 text-xl font-bold text-gray-900">Workflow</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {workflowStats.map((item) => (
            <Card key={item.label} className="group relative overflow-hidden border-0 shadow-soft transition-all hover:shadow-medium">
              {/* Colored accent bar */}
              <div className={`absolute left-0 top-0 h-full w-1 ${item.color}`}></div>
              
              <CardContent className="p-6">
                <div className="mb-4">
                  <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">{item.label}</div>
                </div>
                <div className="mb-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-gray-900">{item.count}</span>
                    {item.amount && (
                      <span className="text-xl font-bold text-gray-600">
                        {formatCurrency(item.amount)}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm font-semibold text-gray-700">{item.status}</p>
                </div>
                <div className="space-y-1.5 border-t border-gray-100 pt-3">
                  {item.details.map((detail, idx) => (
                    <p key={idx} className="text-xs text-gray-600">{detail}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* To Do Section - Modern Design */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 px-6 py-4">
          <CardTitle className="text-xl font-bold text-gray-900">To do</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            <button className="group flex w-full items-center justify-between px-6 py-5 text-left transition-all hover:bg-primary-50/50">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600 transition-colors group-hover:bg-blue-200">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Review 3 new lease applications</p>
                  <p className="mt-0.5 text-sm text-gray-600">Complete background checks and credit reports</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500">5 minutes</span>
                <ArrowRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
            <button className="group flex w-full items-center justify-between px-6 py-5 text-left transition-all hover:bg-primary-50/50">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-600 transition-colors group-hover:bg-red-200">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Follow up on overdue rent payments</p>
                  <p className="mt-0.5 text-sm text-gray-600">2 tenants have outstanding balances</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500">10 minutes</span>
                <ArrowRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
            <button className="group flex w-full items-center justify-between px-6 py-5 text-left transition-all hover:bg-primary-50/50">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600 transition-colors group-hover:bg-green-200">
                  <Wrench className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Schedule maintenance for Garden Row</p>
                  <p className="mt-0.5 text-sm text-gray-600">HVAC inspection due this week</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500">3 minutes</span>
                <ArrowRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Today's Summary - Modern Stats Grid */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-6 py-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold text-gray-900">Today's summary</CardTitle>
            <Button variant="ghost" size="sm" className="font-semibold text-primary-600 hover:text-primary-700">
              View Schedule →
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-5 gap-6">
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Total</p>
              <p className="mt-3 text-4xl font-bold text-gray-900">$18,500</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Collected</p>
              <p className="mt-3 text-4xl font-bold text-green-600">$12,300</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Pending</p>
              <p className="mt-3 text-4xl font-bold text-orange-600">$6,200</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Overdue</p>
              <p className="mt-3 text-4xl font-bold text-red-600">$0</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Expenses</p>
              <p className="mt-3 text-4xl font-bold text-gray-900">$3,450</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Performance Grid - Modern Cards */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Receivables */}
        <Card className="border-0 shadow-soft">
          <CardHeader className="border-b border-gray-100 bg-gray-50/50 px-6 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-gray-900">Receivables</CardTitle>
              <Button variant="ghost" size="sm" className="font-semibold text-primary-600 hover:text-primary-700">
                View all →
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-600">1 client owes you</p>
              <p className="mt-2 text-5xl font-bold text-gray-900">$6,200</p>
            </div>
            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-gray-500">
                <span>Client</span>
                <span>Balance</span>
                <span>Late</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-gray-900">Garden Row Complex</span>
                <span className="font-bold text-gray-900">$6,200</span>
                <span className="font-bold text-green-600">$0</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Rent */}
        <Card className="border-0 shadow-soft">
          <CardHeader className="border-b border-gray-100 bg-gray-50/50 px-6 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-gray-900">Upcoming rent</CardTitle>
              <Button variant="ghost" size="sm" className="font-semibold text-primary-600 hover:text-primary-700">
                View all →
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-600">This week (Tomorrow - Nov 6)</p>
              <div className="mt-2 flex items-baseline gap-3">
                <p className="text-5xl font-bold text-gray-900">$12,400</p>
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                  <TrendingUp className="mr-1 h-3 w-3" />
                  +100%
                </Badge>
              </div>
            </div>
            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-gray-500">
                <span>Property</span>
                <span>Amount</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-gray-900">Main Street Apartments</span>
                <span className="font-bold text-gray-900">$8,200</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-gray-900">Riverside Condos</span>
                <span className="font-bold text-gray-900">$4,200</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart - Modern Professional Style */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-bold text-gray-900">Revenue</CardTitle>
              <p className="mt-1 text-sm font-medium text-gray-500">Jan 1, 2024 - Jul 30, 2025</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-primary-500 shadow-sm"></div>
                <span className="text-sm font-medium text-gray-600">2024</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-purple-500 shadow-sm"></div>
                <span className="text-sm font-medium text-gray-600">2025</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6">
            <p className="text-5xl font-bold text-gray-900">$48,000</p>
            <p className="mt-1 text-sm font-medium text-green-600">↑ 15% from last period</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis 
                dataKey="month" 
                stroke="#9ca3af" 
                tick={{ fontSize: 13, fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                stroke="#9ca3af" 
                tick={{ fontSize: 13, fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)',
                  padding: '12px',
                }}
                cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
              />
              <Bar dataKey="revenue" fill="url(#colorGradient)" radius={[8, 8, 0, 0]} />
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.8} />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
